<template>
  <div class="collection-page">
    <CollectionGate :collection-handle="collectionHandle" />
    <CollectionHeader @typeChange="handleTypeChange" />
    <CollectionToolbar />
    <ProductGrid
      :products="productsWithSkeleton"
      :featured-slots="shouldShowFeaturedSlots ? featuredSlots : []"
      @productClick="nostoProductClick"
    />
    <Pagination v-if="showPagination" @loadMore="handleLoadMore" />
    <BackToTop />
    <Filters v-if="!nostoActive" @filterChange="handleFilterChange" />
    <NostoSearchFilters
      v-else
      :facets="nostoFacets"
      show-sort
      @filterChange="nostoFilterChange"
    />
    <Modal
      v-model:open="showQuiz"
      :images="{
        smallMedia: 'https://placehold.co/390x390/',
        mediumMedia: 'https://placehold.co/390x390/',
        largeMedia: 'https://placehold.co/507x680/',
      }"
    >
      <Quiz
        :steps="[
          {
            component: 'sign-up-forms/v2/Step1.vue',
          },
          {
            component: 'sign-up-forms/v2/Step2.vue',
          },
          {
            component: 'sign-up-forms/v2/Step3.vue',
          },
          {
            component: 'sign-up-forms/v2/Step4.vue',
          },
        ]"
        @close="showQuiz = false"
      >
        <template #header>
          <h2>Would you like to join the Rat &amp; Boa World?</h2>
        </template>
      </Quiz>
    </Modal>
  </div>
</template>

<script>
import CollectionHeader from "@/components/collection/CollectionHeader.vue";
import CollectionToolbar from "@/components/collection/CollectionToolbar.vue";
import ProductGrid from "@/components/collection/ProductGridWithFeatured.vue";
import Pagination from "@/components/collection/Pagination.vue";
import BackToTop from "@/components/BackToTop.vue";
import Filters from "@/components/collection/FiltersV2.vue";
import CollectionGate from "@/components/collection/CollectionGate.vue";

import NostoSearchFilters from "@/components/search/NostoSearchFilters.vue";
import nostoCollection from "@/mixins/nostoCollection";

import collectionQuery from "@/graphql/queries/CollectionV2.gql";
import { PER_PAGE, mergeCollectionProducts } from "@/helpers/pagination";
import { mapGetters, mapState, mapActions } from "vuex";
import isEmpty from "lodash-es/isEmpty";
import { viewCollectionEvent } from "@/helpers/events.js";
import { getFeatureToggles } from "@/helpers/featureToggles.js";
import Quiz from "@/components/Quiz.vue";
import Modal from "@/components/Modal.vue";

export default {
  name: "MidGridFeaturedCollection",
  components: {
    CollectionHeader,
    CollectionToolbar,
    ProductGrid,
    Pagination,
    BackToTop,
    Filters,
    Quiz,
    Modal,
    CollectionGate,
    NostoSearchFilters,
  },
  mixins: [nostoCollection],
  props: {
    collectionHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      lastPaginationCursor: null,
      isFetchingMore: false,
      quizCompleted: false,
      showQuiz: false,
    };
  },
  computed: {
    ...mapState("collection", ["collection", "loading", "filters", "sort"]),
    ...mapGetters("collection", ["products"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapState("auth", ["customer"]),
    shouldShowFeaturedSlots() {
      const { midGridFeaturedShowSlots = true } = getFeatureToggles();
      return midGridFeaturedShowSlots;
    },
    showPagination() {
      return this.collection?.products?.pageInfo?.hasNextPage;
    },
    showQuizCTA() {
      const { enableQuizCTA = true } = getFeatureToggles();
      const hasAnsweredQuestionnaire =
        localStorage.getItem("hasAnsweredQuestionnaire") === "true";

      if (this.quizCompleted) {
        return false;
      }
      if (enableQuizCTA) {
        if (this.isLoggedIn) {
          return !this.customer.questionnaire;
        } else {
          return !hasAnsweredQuestionnaire;
        }
      }
      return false;
    },
    skeletonProducts() {
      return Array.from({ length: PER_PAGE }, (_, index) => ({
        skeleton: true,
        id: `skeleton-${index}`,
      }));
    },
    productsWithSkeleton() {
      const products = [...this.products];
      if (this.loading) {
        return [...products, ...this.skeletonProducts];
      }
      return products;
    },
    featuredSlots() {
      const featured =
        this.collection?.featuredProductCards?.references?.edges?.map(
          ({ node }) => node
        ) || [];

      return featured;
    },
  },
  watch: {
    sort() {
      this.handleSortChange();
    },
  },
  methods: {
    ...mapActions("collection", [
      "setCollection",
      "setLoading",
      "setFilters",
      "setPriceRange",
    ]),
    handleSortChange() {
      if (this.nostoActive) {
        this.nostoRefetch();
        return;
      }

      const { value, reverse } = this.sort;

      this.$apollo.queries.collection.refetch({
        sort: value,
        reverse,
      });
    },
    async handleLoadMore() {
      if (this.nostoActive) {
        return this.nostoLoadMore();
      }

      if (!this.collection?.products?.pageInfo?.hasNextPage) {
        return;
      }

      if (this.loading || this.isFetchingMore) {
        return;
      }

      this.isFetchingMore = true;
      this.setLoading(true);

      let paginationCursor = null;

      try {
        const edges = this.collection.products?.edges;
        const lastEdge = edges?.[edges.length - 1];
        paginationCursor = lastEdge?.cursor;

        if (!paginationCursor) {
          return;
        }

        if (this.lastPaginationCursor === paginationCursor) {
          console.warn("Duplicate pagination cursor");
          return;
        }

        const resp = await this.$apollo.queries.collection.fetchMore({
          variables: {
            collection: this.collectionHandle,
            first: PER_PAGE,
            after: paginationCursor,
          },
        });

        if (resp?.data?.collection) {
          this.setCollection(
            mergeCollectionProducts(this.collection, resp.data.collection)
          );
        }
      } catch (error) {
        console.log(error);
      } finally {
        this.setLoading(false);
        this.isFetchingMore = false;
        if (paginationCursor) {
          this.lastPaginationCursor = paginationCursor;
        }
      }
    },
    handleTypeChange(filters) {
      if (this.nostoTypeChange(filters)) {
        return;
      }

      const keysToFilter = ["productType", "productMetafield"];

      const filtersWithoutTypes = this.filters.filter(
        (filter) => !keysToFilter.includes(Object.keys(filter)[0])
      );

      if (Object.values(filters)[0]) {
        filtersWithoutTypes.push(filters);
      }

      this.setFilters(filtersWithoutTypes);
      this.$apollo.queries.collection.refetch({
        product_filters: filtersWithoutTypes,
      });
    },
    handleFilterChange(filters) {
      this.setFilters(filters);

      this.$apollo.queries.collection.refetch({
        product_filters: filters,
      });
    },
  },
  apollo: {
    collection: {
      query: collectionQuery,
      skip() {
        return !this.collectionHandle;
      },
      variables() {
        return {
          collection: this.collectionHandle,
          first: PER_PAGE,
          sort: "MANUAL",
        };
      },
      result(resp) {
        const data = resp.data;

        if (this.isFetchingMore) {
          return;
        }

        // With Category Merchandising 2.0 on, Nosto owns the product listing
        // and only the collection metadata (header, product types) is taken
        // from Shopify.
        const nostoOwnsListing = this.nostoReceiveShopifyCollection(
          data?.collection
        );

        if (!nostoOwnsListing) {
          this.setCollection(data?.collection);
        }

        viewCollectionEvent(
          data?.collection.title,
          // Only send the Shopify collection id to Nosto when CM2 is on, so the
          // viewCategory payload is unchanged for shoppers with the toggle off.
          this.useNostoCategory ? this.collectionId : null
        );

        if (isEmpty(this.filters)) {
          const filters = data?.collection.products.filters;
          if (filters) {
            const priceFilter = filters.find(
              (filter) => filter.id === "filter.v.price"
            );

            if (priceFilter) {
              const { price } = JSON.parse(priceFilter.values[0].input);
              this.setPriceRange(price);
            }
          }
        }

        if (!nostoOwnsListing) {
          this.setLoading(false);
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.collection-page {
  position: relative;
  /* No fixed height needed for full-screen overlay */
}
</style>
